import { useEffect, useRef, useState } from 'react';
import { getDeviceId, getDeviceInfo } from '@/core/utils/deviceId';
import { generateUUID } from '@/core/utils/uuid';
import { sesionesClient } from '@/core/sdk/modules/administracion/sesionesClient';
import { CONFIGURACION_SESION_MULTIPLE } from '@/config/configuracion';
import { useSesionBloqueada } from '../model/useSesionBloqueada';
import { useAuthStore } from '../store/useAuthStore';  // ✅ Import faltante

/**
 * Tipos de mensaje BroadcastChannel
 */
type BroadcastMessage =
    | { type: 'heartbeat'; sessionId: string; timestamp: number }
    | { type: 'session-closed'; sessionId: string };

/**
 * Hook: Detector de sesión múltiple (cross-device) con persistencia
 * 
 * Detecta si el usuario tiene sesiones activas en:
 * - Múltiples tabs del mismo browser (BroadcastChannel)
 * - Múltiples browsers/dispositivos (API polling con heartbeat)
 * 
 * PERSISTENCIA: Usa Zustand + localStorage para mantener estado de bloqueo
 * incluso si el usuario depura o refresca la página
 * 
 * @returns Estado de detección y función para cerrar otras sesiones
 */
export const useDetectorSesionMultiple = () => {
    const { isAuthenticated } = useAuthStore(); // Added
    const {
        isSesionBloqueada,
        tipoBloqueo,
        dispositivoConflictivo: dispositivoConflictivoStore,
        tabCerradaRemotamente,
        bloquearSesion,
        desbloquearSesion,
        marcarTabCerrada,
    } = useSesionBloqueada();

    const deviceIdRef = useRef<string | null>(null);
    const sessionIdRef = useRef<string | null>(null);
    const channelRef = useRef<BroadcastChannel | null>(null);
    const otherTabsRef = useRef<Set<string>>(new Set());

    // ✅ Configuración desde backend
    const [permitirMultipleSesion, setPermitirMultipleSesion] = useState<boolean | null>(null);
    const [intervaloHeartbeat, setIntervaloHeartbeat] = useState<number>(30000);  // Default 30s

    // ==========================================
    // ✅ OBTENER CONFIGURACIÓN DEL BACKEND
    // ==========================================
    useEffect(() => {
        // Solo cargar configuración si el usuario está autenticado
        if (!isAuthenticated) {
            setPermitirMultipleSesion(false); // Si no está autenticado, no permitir múltiples sesiones por defecto
            return;
        }

        const cargarConfig = async () => {
            try {
                const response = await sesionesClient.obtenerConfiguracion();
                if (response.data) {
                    setPermitirMultipleSesion(response.data.permitirMultipleSesion);
                    setIntervaloHeartbeat(response.data.intervaloHeartbeatMs);
                    {/*console.log('✅ Config backend -', {
                        permitirMultipleSesion: response.data.permitirMultipleSesion,
                        intervaloHeartbeatMs: response.data.intervaloHeartbeatMs
                    });*/}
                } else {
                    throw new Error('Config data es null');
                }
            } catch (error) {
                console.error('❌ Error al cargar config, usando false por defecto:', error);
                setPermitirMultipleSesion(false);  // Seguro por defecto
            }
        };
        cargarConfig();
    }, [isAuthenticated]); // Dependencia en isAuthenticated

    // ==========================================
    // DETECCIÓN LOCAL (BroadcastChannel - tabs mismo browser)
    // ==========================================
    useEffect(() => {
        // ✅ Esperar a que config se cargue
        if (permitirMultipleSesion === null) return;

        // ✅ Si backend permite múltiples sesiones, deshabilitar detector
        if (permitirMultipleSesion) {
            console.log('✅ Sesión múltiple permitida - Detector local deshabilitado');
            return;
        }

        // Si esta tab ya fue cerrada remotamente, no iniciar detector
        if (tabCerradaRemotamente) {
            console.log('🚫 Tab cerrada remotamente - Detector local deshabilitado');
            return;
        }

        // Generar sessionId único para esta tab
        sessionIdRef.current = generateUUID();
        const sessionId = sessionIdRef.current;

        // Crear BroadcastChannel
        const channel = new BroadcastChannel('zafiro-session-monitor');
        channelRef.current = channel;

        // Escuchar mensajes de otras tabs
        channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
            const message = event.data;

            if (message.type === 'heartbeat') {
                // Solo procesar si es de OTRA tab (NO el mensaje propio)
                if (message.sessionId !== sessionId) {
                    // Agregar a set de otras tabs
                    otherTabsRef.current.add(message.sessionId);

                    // Hay otra tab activa
                    console.warn('⚠️ Sesión duplicada detectada (otra tab)', message);
                    bloquearSesion('local');
                }
            } else if (message.type === 'session-closed') {
                // Otra tab fue elegida, esta debe cerrarse
                if (message.sessionId !== sessionId) {
                    console.error('🚫 Esta sesión fue cerrada desde otra pestaña');
                    marcarTabCerrada();
                }
            }
        };

        // Heartbeat local cada 5s
        const localInterval = setInterval(() => {
            channel.postMessage({
                type: 'heartbeat',
                sessionId,
                timestamp: Date.now(),
            } as BroadcastMessage);
        }, CONFIGURACION_SESION_MULTIPLE.INTERVALO_HEARTBEAT_LOCAL);

        // Cleanup
        return () => {
            clearInterval(localInterval);
            channel.close();
            channelRef.current = null;
            otherTabsRef.current.clear();
        };
    }, [tabCerradaRemotamente, permitirMultipleSesion]);

    // ==========================================
    // DETECCIÓN REMOTA (API Polling - otros dispositivos)
    // ==========================================
    useEffect(() => {
        // ✅ Esperar a que config se cargue
        if (permitirMultipleSesion === null) return;

        // ✅ Si backend permite múltiples sesiones, deshabilitar detector
        if (permitirMultipleSesion) {
            console.log('✅ Sesión múltiple permitida - Detector remoto deshabilitado');
            return;
        }

        // Si esta tab ya fue cerrada remotamente, no hacer heartbeat
        if (tabCerradaRemotamente) {
            console.log('🚫 Tab cerrada remotamente - Heartbeat deshabilitado');
            return;
        }

        // Obtener deviceId y device info
        deviceIdRef.current = getDeviceId();
        const deviceInfo = getDeviceInfo();

        const checkRemoteSessions = async () => {
            try {
                const response = await sesionesClient.registrarHeartbeat({
                    dispositivoId: deviceIdRef.current!,
                    navegador: deviceInfo.navegador,
                    sistemaOperativo: deviceInfo.so,
                    tipoDispositivo: deviceInfo.dispositivo,
                    modelo: deviceInfo.modelo,
                });

                // CRÍTICO: Verificar si esta sesión fue cerrada desde otro dispositivo
                if (!response.sesionActiva) {
                    console.error('🚫 Esta sesión fue cerrada desde otro dispositivo');
                    marcarTabCerrada();
                    return; // No continuar procesando
                }

                // Backend retorna si hay otras sesiones activas
                if (response.hayOtrasSesiones && response.otroDispositivo) {
                    console.warn('⚠️ Sesión duplicada detectada (otro dispositivo)', response.otroDispositivo);
                    bloquearSesion('remota', response.otroDispositivo);
                } else {
                    // No hay duplicados - reset estado solo si no está bloqueada localmente
                    if (tipoBloqueo !== 'local') {
                        desbloquearSesion();
                    }
                }
            } catch (error) {
                console.error('❌ Error al verificar sesiones remotas:', error);
                // No marcar como duplicada si hay error de red
            }
        };

        // Heartbeat inicial
        checkRemoteSessions();

        // Heartbeat remoto cada 30s
        const remoteInterval = setInterval(
            checkRemoteSessions,
            intervaloHeartbeat  // ✅ Usar intervalo del backend
        );

        // Cleanup
        return () => {
            clearInterval(remoteInterval);
        };
    }, [tabCerradaRemotamente, tipoBloqueo, permitirMultipleSesion, intervaloHeartbeat, bloquearSesion, desbloquearSesion, marcarTabCerrada]);

    // ==========================================
    // FUNCIÓN: Cerrar otras sesiones
    // ==========================================
    const cerrarOtrasSesiones = async () => {
        if (!deviceIdRef.current) {
            console.error('❌ DeviceId no disponible');
            return;
        }

        try {
            // Si es sesión local, notificar a otras tabs que se cierren
            if (tipoBloqueo === 'local' && channelRef.current) {
                console.log('📢 Notificando a otras tabs que cierren sesión...');
                channelRef.current.postMessage({
                    type: 'session-closed',
                    sessionId: sessionIdRef.current!,
                } as BroadcastMessage);
            }

            // Llamar al backend para cerrar otras sesiones remotas
            await sesionesClient.cerrarOtrasSesiones(deviceIdRef.current);
            console.log('✅ Otras sesiones cerradas correctamente');

            // Desbloquear esta sesión
            desbloquearSesion();
        } catch (error) {
            console.error('❌ Error al cerrar otras sesiones:', error);
            throw error;
        }
    };

    return {
        /**
         * Si hay sesión duplicada detectada o bloqueada persistentemente
         */
        sesionDuplicadaDetectada: isSesionBloqueada,

        /**
         * Tipo de sesión duplicada
         */
        tipo: tipoBloqueo || 'local',

        /**
         * Información del dispositivo conflictivo (solo si es remota)
         */
        dispositivoConflictivo: dispositivoConflictivoStore,

        /**
         * Si esta tab fue cerrada remotamente (bloqueo permanente)
         */
        tabCerradaRemotamente,

        /**
         * Cerrar todas las otras sesiones (mantener solo la actual)
         */
        cerrarOtrasSesiones,
    };
};
